/**
 * Lightweight per-route SEO updater.
 *
 * Why not react-helmet-async?
 *   - We don't SSR. All metadata is set on the client after navigation.
 *   - One small hook keeps the bundle lean and the API obvious.
 *
 * Usage:
 *   useSEO({
 *     title: "Page Title — TheCityPair",
 *     description: "...",
 *     canonical: "/journal",
 *     og: { image: "https://...", type: "article" },
 *     jsonLd: { "@context": "https://schema.org", "@type": "Article", ... },
 *   });
 */
import { useEffect } from "react";

// Production canonical hostname. Override via REACT_APP_SITE_URL if you deploy elsewhere.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://www.thecitypair.com").replace(/\/$/, "");

const DEFAULT_DESCRIPTION =
  "TheCityPair — Premium flight reservations curated for travelers who value time, comfort, and a story worth telling.";

const DEFAULT_OG_IMAGE = `${SITE_URL}/logo-tcp.png`;

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, payload) {
  // Remove any prior JSON-LD scripts we own so route changes don't pile up stale schema.
  document.querySelectorAll('script[type="application/ld+json"][data-tcp]').forEach((n) => n.remove());
  if (!payload) return;
  const blocks = Array.isArray(payload) ? payload : [payload];
  blocks.forEach((schema, idx) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.dataset.tcp = `${id}-${idx}`;
    s.textContent = JSON.stringify(schema);
    document.head.appendChild(s);
  });
}

export function useSEO({ title, description, canonical, og = {}, jsonLd, noindex = false } = {}) {
  useEffect(() => {
    const finalTitle = title || "TheCityPair — Premium Flight Reservations";
    const finalDesc  = description || DEFAULT_DESCRIPTION;
    const path       = canonical || (typeof window !== "undefined" ? window.location.pathname : "/");
    const finalUrl   = path.startsWith("http") ? path : `${SITE_URL}${path}`;
    const finalImg   = og.image || DEFAULT_OG_IMAGE;
    const finalType  = og.type  || "website";

    document.title = finalTitle;

    upsertMeta("name", "description", finalDesc);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    upsertMeta("property", "og:title", finalTitle);
    upsertMeta("property", "og:description", finalDesc);
    upsertMeta("property", "og:url", finalUrl);
    upsertMeta("property", "og:image", finalImg);
    upsertMeta("property", "og:type", finalType);
    upsertMeta("property", "og:site_name", "TheCityPair");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", finalTitle);
    upsertMeta("name", "twitter:description", finalDesc);
    upsertMeta("name", "twitter:image", finalImg);

    upsertLink("canonical", finalUrl);

    upsertJsonLd("route", jsonLd);
  }, [title, description, canonical, og.image, og.type, jsonLd, noindex]);
}

// ---------------------------------------------------------------------------
// Reusable schema.org builders
// ---------------------------------------------------------------------------

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "@id": `${SITE_URL}/#organization`,
    name: "TheCityPair",
    url: SITE_URL,
    logo: `${SITE_URL}/logo-tcp.png`,
    description: DEFAULT_DESCRIPTION,
    areaServed: { "@type": "Country", name: "United States" },
    address: { "@type": "PostalAddress", addressCountry: "US" },
    sameAs: [],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-888-555-7873",
      contactType: "reservations",
      areaServed: "US",
      availableLanguage: ["English"],
    },
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: "TheCityPair",
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function breadcrumbsSchema(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.name,
      item: t.url.startsWith("http") ? t.url : `${SITE_URL}${t.url}`,
    })),
  };
}

export function articleSchema(post) {
  const url = `${SITE_URL}/blog/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    headline: post.title,
    description: post.excerpt,
    image: [post.hero],
    author: { "@type": "Organization", name: post.author || "TheCityPair Editorial" },
    publisher: { "@id": `${SITE_URL}/#organization` },
    datePublished: post.published_at,
    inLanguage: "en-US",
  };
}
